import { useState, useRef, useEffect } from 'react'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import SpeechToTextApi from '../../../../../api/speechToText.api'

const AudioRecorder = ({ onTranscriptionComplete, onError, disabled, variant = 'default' }) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioLevel, setAudioLevel] = useState(0)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const timerRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const animationFrameRef = useRef(null)

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      const mimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/mpeg'
      ]

      let selectedMimeType = 'audio/webm'
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType
          break
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      })

      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        const blobType = selectedMimeType || 'audio/webm'
        const audioBlob = new Blob(audioChunksRef.current, { type: blobType })

        const fileExtension = blobType.includes('mp4') ? 'm4a' :
                             blobType.includes('mpeg') ? 'mp3' :
                             blobType.includes('ogg') ? 'ogg' : 'webm'

        await transcribeAudio(audioBlob, fileExtension)

        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)

      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext
        if (AudioContextClass) {
          const audioContext = new AudioContextClass()
          const source = audioContext.createMediaStreamSource(stream)
          const analyser = audioContext.createAnalyser()
          analyser.fftSize = 256
          source.connect(analyser)

          audioContextRef.current = audioContext
          analyserRef.current = analyser

          const bufferLength = analyser.frequencyBinCount
          const dataArray = new Uint8Array(bufferLength)

          const updateVolume = () => {
            if (!analyserRef.current) return
            analyserRef.current.getByteFrequencyData(dataArray)
            let sum = 0
            for (let i = 0; i < bufferLength; i++) {
              sum += dataArray[i]
            }
            const average = sum / bufferLength
            setAudioLevel(average)
            animationFrameRef.current = requestAnimationFrame(updateVolume)
          }
          animationFrameRef.current = requestAnimationFrame(updateVolume)
        }
      } catch (audioError) {
        console.error('AudioContext failed:', audioError)
      }
    } catch (error) {
      console.error('Error al iniciar grabación:', error)
      onError?.('No se pudo acceder al micrófono. Por favor, verifica los permisos.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    analyserRef.current = null
    setIsRecording(false)
    setAudioLevel(0)
  }

  const transcribeAudio = async (audioBlob, extension = 'webm') => {
    setIsTranscribing(true)
    try {
      const audioFile = new File([audioBlob], `recording.${extension}`, {
        type: audioBlob.type || `audio/${extension}`
      })

      const response = await SpeechToTextApi.transcribe(audioFile, {
        languageCode: 'es-CL',
        enableAutomaticPunctuation: true
      })

      if (response.transcription) {
        onTranscriptionComplete?.(response.transcription)
      } else {
        onError?.('No se pudo transcribir el audio')
      }
    } catch (error) {
      console.error('Error en transcripción:', error)
      onError?.('Error al transcribir el audio. Intenta de nuevo.')
    } finally {
      setIsTranscribing(false)
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleClick = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const isInlineComposer = variant === 'inlineComposer'
  const isInlineComposerLight = variant === 'inlineComposerLight'

  if (isTranscribing) {
    return (
      <button
        type="button"
        disabled
        className={isInlineComposer
          ? 'inline-flex h-7 w-7 cursor-not-allowed items-center justify-center rounded-full bg-transparent text-white/60'
          : isInlineComposerLight
          ? 'inline-flex h-7 w-7 cursor-not-allowed items-center justify-center rounded-full bg-transparent text-muted'
          : 'inline-flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-xl border border-primary/10 bg-white text-muted shadow-[0_8px_18px_rgba(15,23,42,0.05)] sm:h-10 sm:w-10'}
        title="Transcribiendo..."
      >
        <div className={isInlineComposer
          ? 'h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-b-white'
          : isInlineComposerLight
          ? 'h-3.5 w-3.5 animate-spin rounded-full border-2 border-border border-b-primary'
          : 'h-4 w-4 animate-spin rounded-full border-2 border-border border-b-primary sm:h-5 sm:w-5'}></div>
      </button>
    )
  }

  return (
    <div className={isInlineComposer || isInlineComposerLight ? 'flex items-center gap-1.5' : 'flex items-center gap-2'}>
      {isRecording && (
        <div className="flex items-center gap-1.5">
          <span className={isInlineComposer
            ? 'rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white/80'
            : isInlineComposerLight
            ? 'rounded-full bg-danger/10 px-1.5 py-0.5 text-[10px] font-semibold text-danger'
            : 'rounded-full bg-danger/10 px-2 py-1 text-xs font-semibold text-danger sm:text-sm'}>
            {formatTime(recordingTime)}
          </span>
          <div className="flex items-center gap-0.5 h-4 px-1">
            <div className={`w-0.5 rounded-full transition-all duration-75 ${isInlineComposer ? 'bg-white/85' : 'bg-danger'}`} style={{ height: `${Math.max(4, audioLevel * 0.15)}px` }} />
            <div className={`w-0.5 rounded-full transition-all duration-75 ${isInlineComposer ? 'bg-white/85' : 'bg-danger'}`} style={{ height: `${Math.max(4, audioLevel * 0.35)}px` }} />
            <div className={`w-0.5 rounded-full transition-all duration-75 ${isInlineComposer ? 'bg-white/85' : 'bg-danger'}`} style={{ height: `${Math.max(4, audioLevel * 0.2)}px` }} />
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isTranscribing}
        className={isInlineComposer
          ? `inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
              isRecording
                ? 'bg-danger/20 text-danger hover:bg-danger/30'
                : 'bg-transparent text-white/80 hover:bg-white/10 hover:text-white'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`
          : isInlineComposerLight
          ? `inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
              isRecording
                ? 'bg-danger/10 text-danger hover:bg-danger/20'
                : 'bg-transparent text-muted hover:bg-accent2/[0.12] hover:text-accent2'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`
          : `inline-flex h-9 w-9 items-center justify-center rounded-xl border transition-colors sm:h-10 sm:w-10 ${
              isRecording
                ? 'border-danger bg-danger text-white shadow-[0_10px_20px_rgba(220,38,38,0.18)] hover:bg-danger/90'
                : 'border-primary/10 bg-white text-muted shadow-[0_8px_18px_rgba(15,23,42,0.05)] hover:bg-primary/[0.06] hover:text-primary'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        title={isRecording ? 'Detener grabación' : 'Grabar audio'}
      >
        {isRecording ? (
          <StopIcon className={isInlineComposer || isInlineComposerLight ? 'h-4 w-4' : 'h-4 w-4 sm:h-5 sm:w-5'} />
        ) : (
          <MicIcon className={isInlineComposer || isInlineComposerLight ? 'h-4 w-4' : 'h-4 w-4 sm:h-5 sm:w-5'} />
        )}
      </button>
    </div>
  )
}

export default AudioRecorder
